import type { Category } from "@/components/estimation-details/Rooms/Lines";
import type { FilterOptionType } from "@/components/filter/FilterOption";
import type { DataForUI, EstimateDetail, LineItemDetail, Page, BoundingBox } from "@/interfaces/estimate-detail";
import type { Estimation, File, UploadedFileProperty } from "@/interfaces/estimation.interface";
import type { IMissingMapping, IXactimateToolRowData } from "@/interfaces/payloads/xactimate";
import { getKeyForMultiSelect } from "@/utils/utils";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

export interface SelectedLineItem {
  tupleId: string;
  lineItemId: string;
  pageId: string;
  lineItem: string;
}

export interface ConversationContext {
  stopCount: number;
  accumulatedScopeNotes: string[];
  accumulatedAffectedRooms: string[];
  transcripts?: string[];
  projectAddress?: string;
  unfinishedRoomTranscript?: string;
  unfinishedRoomName?: string;
  unansweredQuestion?: string;
  conversationHistory: Array<{
    timestamp: string;
    scopeNotes?: string;
    affectedRooms?: string;
    conversationId: string;
  }>;
}

export interface LineItemXactimateMapping {
  lineItemId: string;
  lineItem: string;
  cat?: string;
  sel?: string;
}

export interface EstimationSliceI {
  isLoading?: boolean;
  isPdfLoading?: boolean;
  files?: File[];
  notes?: string[];
  links?: string[];
  estimation?: Estimation;
  estimationDetail?: EstimateDetail[];
  currentViewEstimates?: EstimateDetail[];
  lineItemDetail?: LineItemDetail;
  currentRoom?: FilterOptionType;
  rightSidebarCurrentTab?: FilterOptionType;
  fullPageImagePath?: string;
  missingMappings?: IMissingMapping[];
  fixedMapping?: IXactimateToolRowData[];
  showMissingMappingModal?: boolean;
  estimationCreated?: Estimation;
  currentViewEstimateId?: string;
  allCurrentViewPages?: {
    page: Page;
    documentIndex: number;
    document: DataForUI;
  }[];
  selectedFiles?: string[];
  region?: BoundingBox;
  previewCategory?: Category;
  lineItemXactimateMapping?: LineItemXactimateMapping[];
  uploadedFilesPropertiesCreated?: UploadedFileProperty[];
  exportedToXactimate?: boolean;
  selectedLineItems?: SelectedLineItem[];
  conversationContext?: { [estimateId: string]: ConversationContext };
  isUpdateScopeNoteLoading?: boolean;
  isUpdateLineItemLoading?: boolean;
  knownLineItemComponentKeys?: string[];
}

const initialState: EstimationSliceI = {
  isLoading: false,
  isPdfLoading: false,
  isUpdateScopeNoteLoading: false,
  isUpdateLineItemLoading: false,
  showMissingMappingModal: false,
  files: [],
  notes: [],
  links: [],
  estimationDetail: undefined,
  missingMappings: [],
  allCurrentViewPages: [],
  currentViewEstimates: [],
  rightSidebarCurrentTab: {
    value: "Details",
    id: "Details",
  },
  selectedFiles: undefined,
  uploadedFilesPropertiesCreated: [],
  exportedToXactimate: false,
  selectedLineItems: [],
  conversationContext: {},
  knownLineItemComponentKeys: [],
};

const estimationSlice = createSlice({
  name: "estimation",
  initialState,
  reducers: {
    setEstimationStateAction(state: EstimationSliceI, action: PayloadAction<EstimationSliceI>) {
      return {
        ...state,
        ...action.payload,
      };
    },
    resetApp() {
      return initialState;
    },
    addToSelection(state: EstimationSliceI, action: PayloadAction<SelectedLineItem>) {
      const item = action.payload;
      const exists = state.selectedLineItems?.some((selected) => getKeyForMultiSelect(selected) === getKeyForMultiSelect(item));
      if (!exists) {
        state.selectedLineItems = [...(state.selectedLineItems || []), item];
      }
    },
    removeFromSelection(state: EstimationSliceI, action: PayloadAction<SelectedLineItem>) {
      const item = action.payload;
      state.selectedLineItems =
        state.selectedLineItems?.filter((selected) => !(getKeyForMultiSelect(selected) === getKeyForMultiSelect(item))) || [];
    },
    toggleSelection(state: EstimationSliceI, action: PayloadAction<{ item: SelectedLineItem; isShiftClick: boolean }>) {
      const { item, isShiftClick } = action.payload;

      if (isShiftClick) {
        // For SHIFT clicks, always add to selection
        const exists = state.selectedLineItems?.some((selected) => getKeyForMultiSelect(selected) === getKeyForMultiSelect(item));
        if (!exists) {
          state.selectedLineItems = [...(state.selectedLineItems || []), item];
        }
      } else {
        // For normal clicks, replace selection
        state.selectedLineItems = [item];
      }
    },
    clearSelection(state: EstimationSliceI) {
      state.selectedLineItems = [];
    },
    updateConversationContext(
      state: EstimationSliceI,
      action: PayloadAction<{ estimateId: string; context: Partial<ConversationContext> }>,
    ) {
      const { estimateId, context } = action.payload;
      const currentContext = state.conversationContext?.[estimateId] || {
        stopCount: 0,
        accumulatedScopeNotes: [],
        accumulatedAffectedRooms: [],
        conversationHistory: [],
      };

      state.conversationContext = {
        ...state.conversationContext,
        [estimateId]: {
          ...currentContext,
          ...context,
        },
      };
    },
    addConversationData(
      state: EstimationSliceI,
      action: PayloadAction<{
        estimateId: string;
        conversationId: string;
        scopeNotes?: string;
        affectedRooms?: string;
        projectAddress?: string;
        unfinishedRoomTranscript?: string;
        unfinishedRoomName?: string;
        unansweredQuestion?: string;
        transcript?: string;
      }>,
    ) {
      const {
        estimateId,
        conversationId,
        scopeNotes,
        affectedRooms,
        projectAddress,
        unfinishedRoomTranscript,
        unfinishedRoomName,
        unansweredQuestion,
        transcript,
      } = action.payload;
      const currentContext = state.conversationContext?.[estimateId] || {
        stopCount: 0,
        accumulatedScopeNotes: [],
        accumulatedAffectedRooms: [],
        conversationHistory: [],
      };

      const newHistory = [
        ...currentContext.conversationHistory,
        {
          timestamp: new Date().toISOString(),
          scopeNotes,
          affectedRooms,
          conversationId,
        },
      ];

      const newScopeNotes = scopeNotes ? [...currentContext.accumulatedScopeNotes, scopeNotes] : currentContext.accumulatedScopeNotes;

      const newAffectedRooms = affectedRooms
        ? [...currentContext.accumulatedAffectedRooms, affectedRooms]
        : currentContext.accumulatedAffectedRooms;

      const newTranscripts = transcript ? [...(currentContext.transcripts || []), transcript] : currentContext.transcripts;

      // If the unfinished room name is the same, concat the unfinished room transcripts.
      const newUnfinishedRoomTranscript =
        unfinishedRoomName?.toLowerCase().replace(/\s/g, "") === currentContext.unfinishedRoomName?.toLowerCase().replace(/\s/g, "")
          ? (currentContext.unfinishedRoomTranscript || "") + (unfinishedRoomTranscript || "")
          : unfinishedRoomTranscript;

      state.conversationContext = {
        ...state.conversationContext,
        [estimateId]: {
          stopCount: currentContext.stopCount + 1,
          accumulatedScopeNotes: newScopeNotes,
          accumulatedAffectedRooms: newAffectedRooms,
          projectAddress: projectAddress || currentContext.projectAddress,
          unfinishedRoomTranscript: newUnfinishedRoomTranscript,
          unansweredQuestion: unansweredQuestion || currentContext.unansweredQuestion,
          unfinishedRoomName: unfinishedRoomName || currentContext.unfinishedRoomName,
          transcripts: newTranscripts,
          conversationHistory: newHistory,
        },
      };
    },
  },
});

export default estimationSlice.reducer;
export const {
  setEstimationStateAction,
  addToSelection,
  removeFromSelection,
  toggleSelection,
  clearSelection,
  addConversationData,
} = estimationSlice.actions;
